import React, { useEffect, useRef, useMemo, useState } from 'react';
import type { Athlete, TrackPoint } from '../types';
import type { OffsetAdjustment } from '../services/raceAnalysis';
import { ArrowsPointingOutIcon } from './icons/PlaybackIcons'; // reusing existing icon for reset

// Chart.js is loaded from CDN
declare const Chart: any;

interface DataChartProps {
  athletes: Athlete[];
  currentTime: number;
  raceStartTime: number;
  metric: 'speed' | 'power' | 'heartRate';
  onMetricChange: (metric: 'speed' | 'power' | 'heartRate') => void;
  highlightedAthleteId: string | null;
  xAxisMetric: 'time' | 'distance';
  onXAxisMetricChange: (metric: 'time' | 'distance') => void;
  showElevationProfile: boolean;
  onShowElevationChange: (show: boolean) => void;
  dynamicOffsets?: Map<string, OffsetAdjustment[]>;
  currentUserId?: string;
}

// Custom Chart.js plugin to draw the vertical line for the current time/distance
const timeIndicatorPlugin = {
  id: 'timeIndicator',
  afterDraw: (chart: any, args: any, options: any) => {
    const { ctx, chartArea: { top, bottom, left, right }, scales: { x } } = chart;
    const xCoord = x.getPixelForValue(options.currentValue);

    if (xCoord >= left && xCoord <= right) {
      ctx.save();
      ctx.strokeStyle = options.color;
      ctx.lineWidth = options.width;
      ctx.beginPath();
      ctx.moveTo(xCoord, top);
      ctx.lineTo(xCoord, bottom);
      ctx.stroke();

      // Draw label
      ctx.fillStyle = options.color;
      ctx.textAlign = 'center';
      ctx.font = '12px sans-serif';
      ctx.fillText('Now', xCoord, top - 5);

      ctx.restore();
    }
  }
};

// Custom Chart.js plugin to draw match events (fire icons)
const matchIndicatorPlugin = {
  id: 'matchIndicator',
  afterDatasetsDraw: (chart: any, args: any, options: any) => {
    const { ctx, chartArea: { left, right }, scales: { x, 'y-metric': yMetric } } = chart;
    const { athletes, xAxisMetric, raceStartTime, metric, dynamicOffsets, currentUserId } = options;

    if (metric !== 'power') return; // Only overlay matches on the Power chart

    ctx.save();
    ctx.font = '18px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    athletes.forEach((athlete: any) => {
      const isCurrentUser = currentUserId && (athlete.userId === currentUserId || athlete.id === currentUserId);
      if (!isCurrentUser) return;

      // Check if the current user's dataset is visible in the chart
      const datasetIndex = chart.data.datasets.findIndex((ds: any) => ds.label === athlete.name);
      if (datasetIndex !== -1 && !chart.isDatasetVisible(datasetIndex)) return;

      if (!athlete.matchEvents || !athlete.matchEvents.length) return;

      const adjustments = dynamicOffsets?.get(athlete.id) || [];

      athlete.matchEvents.forEach((match: any) => {
        let xValue;
        if (xAxisMetric === 'time') {
          xValue = match.time - raceStartTime;
        } else {
          let offset = 0;
          for (let i = adjustments.length - 1; i >= 0; i--) {
            if (adjustments[i].timestamp <= match.time) {
              offset = adjustments[i].distanceOffset;
              break;
            }
          }
          xValue = match.distance + offset;
        }

        const xCoord = x.getPixelForValue(xValue);
        // Place the icon slightly above the power reading
        let yCoord = yMetric.getPixelForValue(match.avgPower) - 10;

        if (xCoord >= left && xCoord <= right) {
          ctx.fillText('🔥', xCoord, yCoord);
        }
      });
    });
    ctx.restore();
  }
};

const calculateMovingAverage = (data: (number | null)[], windowSize: number): (number | null)[] => {
  const result: (number | null)[] = [];
  for (let i = 0; i < data.length; i++) {
    let sum = 0;
    let count = 0;
    // Simple trailing window
    for (let j = 0; j < windowSize; j++) {
      if (i - j >= 0) {
        const val = data[i - j];
        if (val !== null) {
          sum += val;
          count++;
        }
      }
    }
    result.push(count > 0 ? sum / count : null);
  }
  return result;
};

const DataChart: React.FC<DataChartProps> = ({
  athletes,
  currentTime,
  raceStartTime,
  metric,
  onMetricChange,
  highlightedAthleteId,
  xAxisMetric,
  onXAxisMetricChange,
  showElevationProfile,
  onShowElevationChange,
  dynamicOffsets,
  currentUserId,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Zoom State
  const [zoomRange, setZoomRange] = useState<{ min: number, max: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);

  // Reset zoom when metrics change
  useEffect(() => {
    setZoomRange(null);
  }, [xAxisMetric]);

  // Helper to get offset for a specific time
  const getOffsetAtTime = (athleteId: string, time: number) => {
    if (!dynamicOffsets) return 0;
    const adjustments = dynamicOffsets.get(athleteId) || [];
    let distOffset = 0;
    for (let i = adjustments.length - 1; i >= 0; i--) {
      if (adjustments[i].timestamp <= time) {
        distOffset = adjustments[i].distanceOffset;
        break;
      }
    }
    return distOffset;
  };

  const currentXValue = useMemo(() => {
    if (!athletes.length || !athletes[0].track.length) {
      return currentTime;
    }

    if (xAxisMetric === 'time') {
      return currentTime;
    }

    // Find distance at current time for the first athlete to position the 'Now' indicator
    const absoluteTime = raceStartTime + currentTime;
    const track = athletes[0].track;
    let rawDistance = 0;

    if (track && track.length > 0) {
      if (absoluteTime <= track[0].timestamp) rawDistance = track[0].distance;
      else if (absoluteTime >= track[track.length - 1].timestamp) rawDistance = track[track.length - 1].distance;
      else {
        for (let i = 1; i < track.length; i++) {
          const p1 = track[i - 1];
          const p2 = track[i];
          if (absoluteTime >= p1.timestamp && absoluteTime <= p2.timestamp) {
            const timeDiff = p2.timestamp - p1.timestamp;
            const ratio = timeDiff > 0 ? (absoluteTime - p1.timestamp) / timeDiff : 0;
            rawDistance = p1.distance + (p2.distance - p1.distance) * ratio;
            break;
          }
        }
      }
    }

    // Apply offset for the reference athlete
    const offset = getOffsetAtTime(athletes[0].id, absoluteTime);
    return rawDistance + offset;

  }, [xAxisMetric, currentTime, athletes, raceStartTime, dynamicOffsets]);


  const chartData = useMemo(() => {
    if (!athletes.length || raceStartTime === 0) return { datasets: [] };

    const datasets: Record<string, any>[] = athletes.map(athlete => {
      const adjustments = dynamicOffsets?.get(athlete.id) || [];

      // Prepare raw Y values first to apply smoothing if needed
      let rawYValues = athlete.track.map(p => metric === 'speed' ? p[metric] * 0.621371 : p[metric]);

      // Apply 3-second moving average for Power
      if (metric === 'power') {
        rawYValues = calculateMovingAverage(rawYValues, 3);
      }

      const data = athlete.track.map((p, index) => {
        let offset = 0;
        if (xAxisMetric === 'distance') {
          for (let i = adjustments.length - 1; i >= 0; i--) {
            if (adjustments[i].timestamp <= p.timestamp) {
              offset = adjustments[i].distanceOffset;
              break;
            }
          }
        }

        return {
          x: xAxisMetric === 'time' ? p.timestamp - raceStartTime : p.distance + offset,
          y: rawYValues[index],
        };
      });

      return {
        label: athlete.name,
        data: data,
        borderColor: athlete.color || '#34d399',
        backgroundColor: 'transparent',
        pointRadius: 0,
        borderWidth: 2,
        tension: 0.1,
        yAxisID: 'y-metric',
      };
    });

    if (showElevationProfile && athletes.length > 0) {
      const firstAthlete = athletes[0];
      const adjustments = dynamicOffsets?.get(firstAthlete.id) || [];

      const elevationData = firstAthlete.track.map(p => {
        let offset = 0;
        if (xAxisMetric === 'distance') {
          for (let i = adjustments.length - 1; i >= 0; i--) {
            if (adjustments[i].timestamp <= p.timestamp) {
              offset = adjustments[i].distanceOffset;
              break;
            }
          }
        }
        return {
          x: xAxisMetric === 'time' ? p.timestamp - raceStartTime : p.distance + offset,
          y: p.elevation
        }
      });

      datasets.push({
        label: 'Elevation',
        data: elevationData,
        borderColor: '#6b7280',
        backgroundColor: 'rgba(75, 85, 99, 0.3)',
        fill: 'start',
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.2,
        yAxisID: 'y-elevation',
        order: 99,
      });
    }

    return { datasets };
  }, [athletes, metric, xAxisMetric, showElevationProfile, raceStartTime, dynamicOffsets]);

  // --- Zoom Event Handlers ---

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!chartRef.current) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startX = e.clientX - rect.left;
    setDragStart(startX);
    setDragEnd(startX);
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || dragStart === null || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const currentX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    setDragEnd(currentX);
  };

  const handleMouseUp = () => {
    if (!isDragging || dragStart === null || dragEnd === null || !chartRef.current) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    const chart = chartRef.current;
    const { left, right } = chart.chartArea;

    // Ignore small clicks
    if (Math.abs(dragEnd - dragStart) < 10) {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    // Convert pixel to chart value
    const minPixel = Math.max(Math.min(dragStart, dragEnd), left);
    const maxPixel = Math.min(Math.max(dragStart, dragEnd), right);

    const minVal = chart.scales.x.getValueForPixel(minPixel);
    const maxVal = chart.scales.x.getValueForPixel(maxPixel);

    if (minVal !== undefined && maxVal !== undefined && minVal < maxVal) {
      setZoomRange({ min: minVal, max: maxVal });
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  };

  const resetZoom = () => {
    setZoomRange(null);
  }

  // --- Chart Lifecycle ---

  useEffect(() => {
    if (!canvasRef.current || !chartData.datasets.length) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        datasets: chartData.datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        animation: { duration: 0 }, // Disable animation for performance during updates
        scales: {
          x: {
            type: 'linear',
            min: zoomRange ? zoomRange.min : undefined,
            max: zoomRange ? zoomRange.max : undefined,
            title: {
              display: true,
              text: xAxisMetric === 'time' ? 'Time' : 'Distance (mi)',
              color: '#9ca3af'
            },
            ticks: {
              color: '#9ca3af',
              callback: (value: number) => {
                if (xAxisMetric === 'time') {
                  const totalSeconds = Math.floor(value / 1000);
                  const hours = Math.floor(totalSeconds / 3600);
                  const minutes = Math.floor((totalSeconds % 3600) / 60);
                  const seconds = totalSeconds % 60;
                  if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                }
                return (value * 0.000621371).toFixed(1);
              },
            },
            grid: { color: '#4a5568' }
          },
          'y-metric': {
            position: 'left',
            title: {
              display: true,
              text: metric === 'speed' ? 'mph' : metric === 'power' ? 'Watts (3s Avg)' : 'Bpm',
              color: '#9ca3af'
            },
            ticks: { color: '#9ca3af' },
            grid: { color: '#4a5568' }
          },
          'y-elevation': {
            position: 'right',
            title: {
              display: true,
              text: 'Elevation (m)',
              color: '#9ca3af'
            },
            ticks: { color: '#9ca3af' },
            grid: { drawOnChartArea: false },
            display: showElevationProfile ? 'auto' : false,
          }
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              color: '#e5e7eb',
              filter: (legendItem: any) => {
                return legendItem.text !== 'Elevation';
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(17, 24, 39, 0.95)',
            titleColor: '#d1d5db',
            bodyColor: '#e5e7eb',
            borderColor: '#4b5563',
            borderWidth: 1,
            callbacks: {
              title: (tooltipItems: any[]) => {
                const xValue = tooltipItems[0].parsed.x;
                if (xAxisMetric === 'time') {
                  const totalSeconds = Math.floor(xValue / 1000);
                  const hours = Math.floor(totalSeconds / 3600);
                  const minutes = Math.floor((totalSeconds % 3600) / 60);
                  const seconds = totalSeconds % 60;
                  if (hours > 0) return `Time: ${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                  return `Time: ${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                }
                return `Distance: ${(xValue * 0.000621371).toFixed(2)} mi`;
              },
              label: (context: any) => {
                let label = context.dataset.label || '';
                if (label) { label += ': '; }
                if (context.parsed.y !== null) {
                  let unit = '';
                  if (context.dataset.yAxisID === 'y-elevation') {
                    unit = ' m';
                  } else {
                    unit = metric === 'speed' ? ' mph' : metric === 'power' ? ' W (3s)' : ' bpm';
                  }
                  label += Math.round(context.parsed.y) + unit;
                }
                return label;
              },
              afterBody: (tooltipItems: any[]) => {
                if (metric !== 'power') return null;

                // Find if the current user's dataset is among the hovered items (meaning it's visible and hovered)
                const currentUserItem = tooltipItems.find(item => {
                  const a = athletes.find((ath: any) => ath.name === item.dataset.label);
                  return a && currentUserId && (a.userId === currentUserId || a.id === currentUserId);
                });

                if (!currentUserItem) return null; // Current user is either not hovered or their graph is hidden

                const athlete = athletes.find(a => a.name === currentUserItem.dataset.label);
                if (!athlete || !athlete.matchEvents) return null;

                const xValue = currentUserItem.parsed.x;
                const match = athlete.matchEvents.find((m: any) => {
                  let mx;
                  if (xAxisMetric === 'time') {
                    mx = m.time - raceStartTime;
                  } else {
                    const adjustments = dynamicOffsets?.get(athlete.id) || [];
                    let offset = 0;
                    for (let i = adjustments.length - 1; i >= 0; i--) {
                      if (adjustments[i].timestamp <= m.time) {
                        offset = adjustments[i].distanceOffset;
                        break;
                      }
                    }
                    mx = m.distance + offset;
                  }

                  // Use a generous threshold since tooltips snap to the nearest data point
                  const threshold = xAxisMetric === 'time' ? 2500 : 15;
                  return Math.abs(mx - xValue) <= threshold;
                });

                if (match) {
                  const activeDuration = match.activeBurnDuration ? Math.round(match.activeBurnDuration) : Math.round(match.durationSeconds);
                  const activePower = match.activeBurnAvgPower ? Math.round(match.activeBurnAvgPower) : Math.round(match.avgPower);
                  const formattedActiveDuration = activeDuration >= 60
                    ? `${Math.floor(activeDuration / 60)}:${(activeDuration % 60).toString().padStart(2, '0')}`
                    : `${activeDuration}s`;

                  return `\n🔥 Match Burned: ${activePower}W for ${formattedActiveDuration}`;
                }
                return null;
              }
            }
          },
          timeIndicator: {
            currentValue: currentXValue,
            color: '#f59e0b',
            width: 2,
          },
          matchIndicator: {
            athletes,
            xAxisMetric,
            raceStartTime,
            metric,
            dynamicOffsets,
            currentUserId
          }
        }
      },
      plugins: [timeIndicatorPlugin, matchIndicatorPlugin],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartData, zoomRange]); // Re-render when zoomRange changes

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.data.datasets.forEach((dataset: any) => {
      const athlete = athletes.find(a => a.name === dataset.label);
      const isHighlighted = athlete && athlete.id === highlightedAthleteId;
      if (dataset.yAxisID === 'y-metric') {
        dataset.borderWidth = isHighlighted ? 4 : 2;
        dataset.order = isHighlighted ? -1 : 0;
      }
    });

    chart.options.plugins.timeIndicator.currentValue = currentXValue;
    chart.options.plugins.matchIndicator.athletes = athletes;
    chart.options.plugins.matchIndicator.xAxisMetric = xAxisMetric;
    chart.options.plugins.matchIndicator.metric = metric;
    chart.options.plugins.matchIndicator.dynamicOffsets = dynamicOffsets;
    chart.options.plugins.matchIndicator.currentUserId = currentUserId;

    chart.update('none');
  }, [currentXValue, highlightedAthleteId, athletes, currentUserId]);


  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex justify-between items-center mb-2 flex-wrap gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-4">
          <div className="bg-gray-700 rounded-lg p-1 flex items-center gap-1">
            <button
              onClick={() => onMetricChange('speed')}
              className={`px-3 py-1 text-sm rounded-md ${metric === 'speed' ? 'bg-cyan-500 text-white' : 'text-gray-300'}`}
            >Speed</button>
            <button
              onClick={() => onMetricChange('power')}
              className={`px-3 py-1 text-sm rounded-md ${metric === 'power' ? 'bg-cyan-500 text-white' : 'text-gray-300'}`}
            >Power</button>
            <button
              onClick={() => onMetricChange('heartRate')}
              className={`px-3 py-1 text-sm rounded-md ${metric === 'heartRate' ? 'bg-cyan-500 text-white' : 'text-gray-300'}`}
            >Heart Rate</button>
          </div>
          <div className="bg-gray-700 rounded-lg p-1 flex items-center gap-1">
            <span className="text-gray-400 text-xs font-semibold px-2 hidden sm:inline">X-Axis:</span>
            <button onClick={() => onXAxisMetricChange('time')} className={`px-2 py-1 rounded-md ${xAxisMetric === 'time' ? 'bg-cyan-500 text-white' : 'text-gray-300'}`}>Time</button>
            <button onClick={() => onXAxisMetricChange('distance')} className={`px-2 py-1 rounded-md ${xAxisMetric === 'distance' ? 'bg-cyan-500 text-white' : 'text-gray-300'}`}>Distance</button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {zoomRange && (
            <button
              onClick={resetZoom}
              className="flex items-center gap-1 px-3 py-1 bg-gray-600 text-white text-xs rounded-md hover:bg-gray-500 transition-colors animate-fade-in"
            >
              <ArrowsPointingOutIcon className="w-3 h-3" />
              Reset Zoom
            </button>
          )}
          <span className="font-semibold text-gray-400">Elevation Profile</span>
          <button
            onClick={() => onShowElevationChange(!showElevationProfile)}
            className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-cyan-500 ${showElevationProfile ? 'bg-cyan-600' : 'bg-gray-600'
              }`}
            aria-label="Toggle elevation profile"
          >
            <span
              className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${showElevationProfile ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
          </button>
        </div>
      </div>

      {/* Chart Container with Drag Handler */}
      <div
        ref={containerRef}
        className="flex-grow relative min-h-0 select-none cursor-crosshair"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <canvas ref={canvasRef}></canvas>

        {/* Drag Overlay Selection Box */}
        {isDragging && dragStart !== null && dragEnd !== null && (
          <div
            className="absolute top-0 bottom-8 bg-cyan-500/20 border-l border-r border-cyan-400/50 pointer-events-none"
            style={{
              left: Math.min(dragStart, dragEnd),
              width: Math.abs(dragEnd - dragStart)
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DataChart;